import { createFeatureSelector, createSelector } from '@ngrx/store';
import { AppTemplate } from '../../models/app-template.class';
import { AssetData } from '../../models/assetdata.class';
import { AssetService } from '../../models/assetservice.class';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { Entity } from '../../models/entity.class';

import { getCurrentOrganizationId$ } from './session.selector';
import { getSessionUserPermissions$ } from './permissions.selector';
import { APPS_REDUCER_KEY } from '../reducers/apps.reducer';
import { AppsState } from '../states/apps.state';

const currentState = createFeatureSelector<AppsState>(APPS_REDUCER_KEY);

export const getAppHelpDocuments$ = createSelector(currentState, state => state.appsHelpDocumentsByServiceId);

export const getBridgeNodesMap$ = createSelector(currentState, state => state.bridgeServices);

export const getAssetsWithLatestVersion$ = createSelector(currentState, state => state.assetsWithLatestVersion);

export const getSelectedAppServiceId$ = createSelector(currentState, state => state?.selectedApp?.serviceId);

export const getSelectedAppRestApiName$ = createSelector(currentState, state => state?.selectedApp?.restApiName);

const getSelectedPackageServiceId$ = createSelector(currentState, state => state?.selectedApp?.packageServiceId);

const getSelectedBridgeServiceId$ = createSelector(currentState, state => state?.selectedApp?.bridgeServiceId);

export const getPackagedAppsMap$ = createSelector(currentState, state => state.packagedAppsMap);

export const getAppsMetaDataMap$ = createSelector(currentState, state => state.assetsMetaData);

export const getBridgeNodes$ = (assetMetaUId: AssetService['assetMetaUId'], serviceId?: string) =>
    createSelector(
        getBridgeNodesMap$,
        getAssetsWithLatestVersion$,
        getSelectedAppServiceId$,
        (nodesMap, version, selectedServiceId) => nodesMap?.[assetMetaUId || version?.[serviceId || selectedServiceId]?.metadataId]
    );

export const getUnmappedServicesMap$ = createSelector(
    currentState,
    getCurrentOrganizationId$,
    (state, organizationId) => state.nonMappedServices?.[organizationId] || []
);

export const getOnlySubscribedApps$ = createSelector(currentState, getCurrentOrganizationId$, (state, organizationId) => {
    return state.subscribedServices?.[organizationId] || [];
});

export const getSubscribedApps$ = createSelector(getOnlySubscribedApps$, getUnmappedServicesMap$, (apps, nonMappedServices) => {
    return [...apps, ...(nonMappedServices || [])];
});

const getServicesMap$ = createSelector(getSubscribedApps$, apps =>
    apps?.reduce((map, app) => {
        map[app.serviceId] = app;
        return map;
    }, {} as { [property: string]: AssetService })
);

export const getSubscribedAppsMap$ = createSelector(
    getServicesMap$,
    getSubscribedApps$,
    getBridgeNodesMap$,
    getPackagedAppsMap$,
    getAssetsWithLatestVersion$,
    getSelectedAppServiceId$,
    (appsMap, apps, bridgesMap, packagedAppsMap, version, serviceId) => {
        return {
            ...(appsMap || {}),
            ...Object.keys(bridgesMap || {}).reduce((nodesMap, assetMetaUId) => {
                const id = assetMetaUId || version?.[serviceId]?.metadataId;
                const app = apps?.find(service => service.assetMetaUId || version?.[service.serviceId]?.metadataId === id);
                Object.keys(bridgesMap[id] || {}).forEach(nodeId => {
                    const node = bridgesMap[id][nodeId];
                    nodesMap[node.serviceId] = JSON.parse(JSON.stringify(node));
                    nodesMap[node.serviceId].bridge = app;
                    nodesMap[node.serviceId].assetMetaUId = node.id;
                });
                return nodesMap;
            }, {}),
            ...Object.values(packagedAppsMap || {}).reduce((nodesMap, packageApp) => {
                packageApp?.forEach(app => {
                    nodesMap[app.serviceId] = JSON.parse(JSON.stringify(app));
                    nodesMap[app.serviceId].package = packageApp;
                    nodesMap[app.serviceId].assetMetaUId = app.id;
                });
                return nodesMap;
            }, {}),
        } as { [property: string]: AssetService };
    }
);

export const getServicesApiMap$ = createSelector(getSubscribedApps$, apps =>
    apps?.reduce((map, app) => {
        map[app.restApiName] = app;
        return map;
    }, {} as { [property: string]: AssetService })
);

export const getSubscribedAppsApiMap$ = createSelector(
    getServicesApiMap$,
    getSubscribedApps$,
    getBridgeNodesMap$,
    getPackagedAppsMap$,
    (appsMap, apps, bridgesMap, packagesMap) => {
        return {
            ...(appsMap || {}),
            ...Object.keys(bridgesMap || {}).reduce((nodesMap, bridgeAppMetaUId, version) => {
                const bridgeApp = apps?.find(
                    service => service.assetMetaUId || version?.[service.serviceId]?.metadataId === bridgeAppMetaUId
                );
                Object.keys(bridgesMap[bridgeAppMetaUId] || {}).forEach(nodeId => {
                    const node = bridgesMap[bridgeAppMetaUId][nodeId];
                    if (!node.restApiName) {
                        return;
                    }
                    nodesMap[node.restApiName] = JSON.parse(JSON.stringify(node));
                    nodesMap[node.restApiName].bridge = bridgeApp;
                    nodesMap[node.restApiName].assetMetaUId = node.id;
                });
                return nodesMap;
            }, {}),
            ...Object.keys(packagesMap || {}).reduce((nodesMap, packageServiceId) => {
                Object.keys(packagesMap[packageServiceId] || {}).forEach(nodeId => {
                    const node = packagesMap[packageServiceId][nodeId];
                    nodesMap[node.restApiName] = JSON.parse(JSON.stringify(node));
                    nodesMap[node.restApiName].package = packagesMap[packageServiceId];
                    nodesMap[node.restApiName].assetMetaUId = node.id;
                });
                return nodesMap;
            }, {}),
        } as { [property: string]: AssetService };
    }
);

export const getApp$ = (serviceId: string) => createSelector(getSubscribedAppsMap$, appsMap => appsMap?.[serviceId]);

export const getAppByApiName$ = (restApiName: string) =>
    createSelector(getSubscribedApps$, apps => apps?.find(app => app.restApiName === restApiName));

export const getAppsTemplate$ = createSelector(
    currentState,
    getCurrentOrganizationId$,
    (state, organizationId) => state?.appsTemplate?.[organizationId]
);

export const getAppTemplate$ = (serviceId: string) =>
    createSelector(getAppsTemplate$, appsTemplate => appsTemplate?.[serviceId]?.uiTemplate);

export const getTransformedAppTemplate$ = (serviceId: string) =>
    createSelector(
        getAppTemplate$(serviceId),
        getAppsMetaDataMap$,
        getSubscribedAppsMap$,
        getAssetsWithLatestVersion$,
        (template, metaDataMap, appsMap, version) => {
            const app = appsMap[serviceId];
            const assetId = app?.assetMetaUId || version?.[app?.serviceId]?.metadataId;
            return new AppTemplate(metaDataMap?.[serviceId]?.[assetId], template);
        }
    );

export const getSelectedAppEntityTemplate$ = (serviceId: string) =>
    createSelector(getTransformedAppTemplate$(serviceId), getSelectedAppEntityId$, (template, entityid) => {
        return getEntity(template?.entities || [], entityid);
    });

export const getEntity = (entities: AppTemplate['entities'] | Entity[], entityId: string) => {
    let entityFound: AppTemplate['entities'][0] = undefined;
    entities?.forEach(entity => {
        if (entityFound) {
            return;
        }
        if (entity.uid === entityId) {
            entityFound = entity;
            return;
        }
        entity?.entities?.length > 0 && (entityFound = getEntity(entity.entities, entityId));
    });
    return entityFound;
};

export const getAppConfiguratorsMap$ = createSelector(currentState, state => state?.appConfigurators);

export const getAppMetaData$ = (serviceId: string, assetMetaUId?: string) =>
    createSelector(
        getAppsMetaDataMap$,
        getAppTemplate$(serviceId),
        getSubscribedAppsMap$,
        getAppConfiguratorsMap$,
        getAssetsWithLatestVersion$,
        getSessionUserPermissions$,
        (metaDataMap, appTemplate, appsMap, appConfiguratorsMap, version, userPermissions) => {
            const app = appsMap?.[serviceId];
            const assetId = assetMetaUId || app?.assetMetaUId || version?.[app?.serviceId]?.metadataId;
            const metaData = metaDataMap?.[serviceId]?.[assetId] || {};
            const meta = metaData && JSON.parse(JSON.stringify(metaData));
            const template = JSON.parse(JSON.stringify((appTemplate as any) || {}));
            const assetData = new AssetData(CommonUtilsService.cloneObject(meta), template);
            assetData.configurator = appConfiguratorsMap?.[serviceId]?.[assetId];
            assetData.canCreateNewProcess =
                userPermissions?.['ASSET']?.[serviceId]?.permissions['NEW_PROCESS'] !== undefined && !app?.suspended;
            (appTemplate?.uid || appTemplate?.entities) &&
                meta?.uid &&
                assetData?.configurator !== undefined &&
                (assetData.templateUpdated = true);
            return assetData;
        }
    );

export const getAppsMetaDataPermissionsMap$ = createSelector(currentState, state => state.assetsMetaDataPermissions);

export const getAppMetaDataWorkflow$ = (serviceId: string) =>
    createSelector(getAppMetaData$(serviceId), assetData => assetData?.workflowMetadata);

export const getAppPrimaryEntity$ = (serviceId: string) =>
    createSelector(getAppMetaData$(serviceId), metaData => metaData?.entities.find(entity => entity.primary));

export const getReconConfigurations$ = createSelector(currentState, state => state.reconConfiguration);

export const getReconConfiguration$ = (serviceId: string) => createSelector(getReconConfigurations$, configs => configs?.[serviceId]);

export const getAppsLoading$ = createSelector(currentState, state => state.loading);

export const getAppLoadingStatus$ = (serviceId: string) => createSelector(getAppsLoading$, state => state?.[serviceId]);

export const getSelectedApp$ = createSelector(
    getSelectedAppServiceId$,
    getSubscribedAppsMap$,
    getAssetsWithLatestVersion$,
    (serviceId, appsMap, versionsMap) => {
        return {
            ...appsMap?.[serviceId],
            assetMetaUId: appsMap?.[serviceId]?.assetMetaUId || versionsMap?.[serviceId]?.metadataId,
        };
    }
);

export const getSelectedPackageApp$ = createSelector(getSelectedPackageServiceId$, getSubscribedAppsMap$, (serviceId, appsMap) => {
    return appsMap?.[serviceId];
});

export const getSelectedPackageAssetId$ = createSelector(
    getSelectedPackageServiceId$,
    getSubscribedAppsMap$,
    getAssetsWithLatestVersion$,
    (serviceId, appsMap, versionsMap) => appsMap?.[serviceId]?.assetMetaUId || versionsMap?.[serviceId]?.metadataId
);

export const getSelectedBridgeApp$ = createSelector(
    getSelectedBridgeServiceId$,
    getSubscribedAppsMap$,
    (serviceId, appsMap) => appsMap?.[serviceId]
);

export const getSelectedBridgeAssetId$ = createSelector(
    getSelectedBridgeServiceId$,
    getSubscribedAppsMap$,
    getAssetsWithLatestVersion$,
    (serviceId, appsMap, versionsMap) => appsMap?.[serviceId]?.assetMetaUId || versionsMap?.[serviceId]?.metadataId
);

export const getSelectedAppAssetId$ = createSelector(
    getSelectedAppServiceId$,
    getSubscribedAppsMap$,
    getAssetsWithLatestVersion$,

    (serviceId, appsMap, versionsMap) => appsMap?.[serviceId]?.assetMetaUId || versionsMap?.[serviceId]?.metadataId
);

export const getSelectedAppCategory$ = createSelector(currentState, state => state?.selectedApp?.category);

export const getSelectedAppEntityId$ = createSelector(currentState, state => state?.selectedApp?.entityId);

export const getSelectedAppMetaData$ = createSelector(
    getAppsMetaDataMap$,
    getAppsTemplate$,
    getSelectedAppServiceId$,
    getSelectedAppAssetId$,
    getAssetsWithLatestVersion$,
    (metaDataMap, appTemplatesMap, serviceId, assetId, versionsMap) =>
        transformMetaDataWithTemplate(metaDataMap, appTemplatesMap, serviceId, assetId, versionsMap)
);

export const transformMetaDataWithTemplate = (
    metaDataMap: {
        [property: string]: {
            [property: string]: AssetData;
        };
    },
    appTemplatesMap: {
        [property: string]: {
            isOfCurrentOrg: boolean;
            uiTemplate: AppTemplate;
        };
    },
    serviceId: string,
    assetId: string,
    versionsMap?: AppsState['assetsWithLatestVersion']
) => {
    const appTemplate = appTemplatesMap?.[serviceId]?.uiTemplate;
    const metadataId = assetId || versionsMap?.[serviceId]?.metadataId;
    const meta = JSON.parse(JSON.stringify(metaDataMap?.[serviceId]?.[metadataId] || {}));
    const template = JSON.parse(JSON.stringify((appTemplate as any) || {}));
    const assetData = new AssetData(meta, template);
    (appTemplate?.uid || appTemplate?.entities) && meta?.uid && (assetData.templateUpdated = true);
    return assetData;
};

export const getSelectedAppEntity$ = createSelector(getSelectedAppMetaData$, getSelectedAppEntityId$, (metaData, entityId) =>
    getSelectedEntity(metaData?.entities, entityId)
);

const getSelectedEntity = (entities: Entity[], id: string) => {
    let selectedEntity: Entity;
    entities?.find(entity => {
        if (selectedEntity) {
            return;
        }
        selectedEntity = isSelectedEntity(entity, id);
    });
    return selectedEntity;
};

const isSelectedEntity = (entity: Entity, id: string) => {
    if (entity.uid === id) {
        return entity;
    }
    return entity?.entities?.length > 0 && getSelectedEntity(entity.entities, id);
};

export const getSelectedBridgeMetaData$ = createSelector(
    getSelectedBridgeServiceId$,
    getSelectedBridgeAssetId$,
    getAppsMetaDataMap$,
    getAppsTemplate$,
    (serviceId, assetId, metaDataMap, appTemplatesMap) => transformMetaData(serviceId, assetId, metaDataMap, appTemplatesMap)
);

const transformMetaData = (
    serviceId: string,
    assetId: string,
    metaDataMap: AppsState['assetsMetaData'],
    appTemplatesMap: AppsState['appsTemplate']['111']
) => {
    const appTemplate = appTemplatesMap?.[serviceId]?.uiTemplate;
    const meta = JSON.parse(JSON.stringify(metaDataMap?.[serviceId]?.[assetId] || {}));
    const template = JSON.parse(JSON.stringify((appTemplate as any) || {}));
    const assetData = new AssetData(meta, template);
    (appTemplate?.uid || appTemplate?.entities) && meta?.uid && (assetData.templateUpdated = true);
    return assetData;
};

export const getAppDisplayName$ = (serviceId: string) =>
    createSelector(getApp$(serviceId), getAppTemplate$(serviceId), (app, template) => template?.displayName || app?.displayName);

export const getAssetsForSubscriptionApprovalFilters$ = createSelector(currentState, state => state.assetsForSubscriptionApprovalFilters);

export const getSelectedPackageApps$ = createSelector(getSelectedPackageApp$, getPackagedAppsMap$, (app, apps) => apps?.[app?.serviceId]);

export const getTaxonomyApp$ = createSelector(currentState, getAssetsWithLatestVersion$, (state, version) => {
    const app = state.taxonomyApp;
    const assetId = app?.assetMetaUId || version?.[app?.serviceId]?.metadataId;
    return app?.restApiName
        ? {
              ...app,
              assetMetaUId: assetId,
          }
        : undefined;
});
